import * as ort from 'onnxruntime-web'
import { OcrConfig, ImageDataLike } from './ocr-types'
import { preprocessImage } from './ocr-preprocess'
import { greedyCtcDecode, flattenLogits, CtcDecodeResult } from './ocr-decoder'
import { downloadModelBuffer } from './ocr-model-loader'

export class OcrRecognizer {
  private session: ort.InferenceSession | null = null
  private _config: OcrConfig
  private idToToken: Record<number, string> = {}
  private baseUrl: string
  private initialized = false

  constructor(config: OcrConfig, baseUrl: string) {
    this._config = config
    this.baseUrl = baseUrl
  }

  get isInitialized(): boolean {
    return this.initialized
  }

  get config(): OcrConfig {
    return this._config
  }

  async initialize(idToToken: Record<number, string>): Promise<void> {
    if (this.initialized) return

    this.idToToken = idToToken
    this._config.vocabSize = Object.keys(idToToken).length

    ort.env.wasm.wasmPaths = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.25.1/dist/'
    ort.env.wasm.numThreads = 0

    const modelBuffer = await downloadModelBuffer(this.baseUrl, 'recognizer_encoder.onnx')

    this.session = await ort.InferenceSession.create(modelBuffer, {
      executionProviders: ['webgpu', 'webgl', 'wasm'],
      graphOptimizationLevel: 'all',
      enableCpuMemArena: true,
      enableMemPattern: true,
      freeDimensionOverrides: { batch: 1 },
    })

    this.initialized = true
  }

  async recognize(lineImage: ImageDataLike): Promise<CtcDecodeResult> {
    if (!this.session || !this.initialized) {
      throw new Error('Recognizer not initialized')
    }

    try {
      const tensor = preprocessImage(lineImage, this._config)

      const inputTensor = new ort.Tensor('float32', tensor, [1, 1, this._config.imgH, this._config.imgW])

      if ((window as any).__OCR_DEBUG__) {
        console.groupCollapsed(`[OCR Recognize] input: 1×1×${this._config.imgH}×${this._config.imgW}`)
        console.log('Line image:', lineImage.width, 'x', lineImage.height)
      }

      const outputs = await this.session.run({ input: inputTensor })
      const logitsOutput = outputs.logits

      let flatLogits: Float32Array
      if (logitsOutput instanceof ort.Tensor) {
        flatLogits = new Float32Array(logitsOutput.data as Float32Array)
      } else {
        flatLogits = flattenLogits(logitsOutput as unknown as number[][][])
      }

      if ((window as any).__OCR_DEBUG__) {
        const C = this._config.ctcClasses
        const T = Math.floor(flatLogits.length / C)
        console.log('Logits shape:', `[${T}, ${C}]`)
        console.groupEnd()
      }

      const result = greedyCtcDecode(flatLogits, this._config, this.idToToken)

      if ((window as any).__OCR_DEBUG__) {
        console.log(`[OCR Result] text="${result.text}" confidence:${result.confidence.toFixed(3)}`)
      }

      return result
    } catch (err) {
      if (err instanceof Error && err.message.includes('session')) {
        this.session = null
        this.initialized = false
      }
      throw err
    }
  }

  dispose(): void {
    if (this.session) this.session.release()
    this.session = null
    this.initialized = false
    this.idToToken = {}
  }
}
